import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import * as R from 'ramda'
import { Defaults, type Nil, RoleName, useFields } from '@pbt/pbt-ui-components'
import { getPersonString } from '@pbt/pbt-ui-components/src/utils'

import { ConversationTransport, Type } from '~/api/graphql/generated/types'
import {
  CreatedConversationFlow,
  type CreatedConversationsInfoDialogProps,
} from '~/components/dashboard/communications/common/CreatedConversationsInfoDialog'
import type { ConversationMessageFormattingAreaHandle } from '~/components/dashboard/communications/ConversationMessageFormattingArea'
import {
  ConversationCategory,
  INITIAL_COMMUNICATION_FILTERS,
  MessageType,
  MessageWay,
} from '~/constants/communications'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { BaseRoute } from '~/constants/routes'
import useGetPreferredContactMethod from '~/hooks/useGetPreferredContactMethod'
import { fetchMember } from '~/store/actions/members'
import { getContact } from '~/store/duck/contacts'
import { getSmsCommunicationsEnabled } from '~/store/reducers/auth'
import { getBusiness } from '~/store/reducers/businesses'
import { getMembersIsFetchingMemberById } from '~/store/reducers/members'
import { getUser } from '~/store/reducers/users'
import type { ChatMessage, Recipient, TableFilter } from '~/types'
import { useBoopEnabled } from '~/utils/boop'
import {
  getRecipientName,
  getTransportSelectOption,
} from '~/utils/communicationsUtils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'
import useHasRole from '~/utils/useHasRole'

import type { RootState } from '..'
import {
  closeMessageFormattingArea,
  messageFormattingAreaFocused,
} from '../actions/conversationMessages'
import {
  fetchClientConversationsList,
  fetchConversationsList,
  fetchMoreItemsForClientConversationsList,
  fetchMoreItemsForConversationsList,
  setConversationsListFilters,
  setIsReadForConversation,
} from '../actions/conversations'
import { getConstantsIsLoading, getFeatureToggle } from '../reducers/constants'
import {
  getAttachmentsToSend,
  getEditMessage,
  getMessage,
  getMessageFormattingAreaExpanded,
} from '../reducers/conversationMessages'
import {
  getAreConversationsCreated,
  getConversationById,
  getConversationsError,
  getConversationsListFilters,
} from '../reducers/conversations'
import { getTimetableEvent } from '../reducers/timetable'
import { useCanSendSmsToClient } from './clients'
import {
  getArchiveActionTitle,
  getConversationFormattingState,
  getMessageAction,
  getSendActionName,
  handleArchiveToggle,
  handleInitialLocationState,
  openNewThreadReply,
  syncIsMedicalValue,
  syncPreferredTransport,
  validateMessageSend,
} from './messageFormattingHelpers'

export interface TransportOptions {
  disabled: boolean
  hidden: boolean
  label: string
  value: string
}

export function useConversationList({
  clientId,
  patientId,
  queryFrom,
  queryTo,
  fillPrecedingWithNull,
}: {
  clientId?: string | Nil
  fillPrecedingWithNull?: boolean
  patientId?: string | Nil
  queryFrom?: number
  queryTo?: number
}) {
  const dispatch = useDispatch()

  const filters = useSelector(getConversationsListFilters)
  const isConstantsLoading = useSelector(getConstantsIsLoading)
  const isSuppressAllSystemMessagesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SUPPRESS_ALL_SYSTEM_MESSAGES),
  )

  const applyFilter = (filterName: string, filterValue: TableFilter) => {
    dispatch(
      setConversationsListFilters(
        filterValue.value
          ? { ...filters, [filterName]: filterValue }
          : R.omit([filterName], filters),
      ),
    )
  }

  const clearFilters = () => {
    const initialFilters: { [key: string]: { value: any } } =
      INITIAL_COMMUNICATION_FILTERS

    if (isSuppressAllSystemMessagesEnabled) {
      initialFilters.excludeSystemMessages = { value: true }
    }

    dispatch(setConversationsListFilters(initialFilters))
  }

  useEffect(() => {
    const initialFilters: { [key: string]: { value: any } } =
      isSuppressAllSystemMessagesEnabled
        ? {
            ...INITIAL_COMMUNICATION_FILTERS,
            excludeSystemMessages: { value: true },
          }
        : INITIAL_COMMUNICATION_FILTERS

    dispatch(
      setConversationsListFilters({
        ...initialFilters,
        ...filters,
        category: {
          value: filters.category?.value || ConversationCategory.INBOX,
        },
        patientIds: { value: null },
      }),
    )
  }, [isSuppressAllSystemMessagesEnabled])

  useEffect(() => {
    const action = clientId
      ? // TODO: add endpoint for fetching client/patient conversations based on ? patientId
        fetchClientConversationsList(
          clientId,
          queryFrom || 0,
          queryTo || Defaults.CONVERSATIONS_BATCH_LOAD_COUNT,
          fillPrecedingWithNull,
        )
      : fetchConversationsList(
          queryFrom || 0,
          queryTo || Defaults.CONVERSATIONS_BATCH_LOAD_COUNT,
          fillPrecedingWithNull,
        )
    dispatch(action)
  }, [filters, clientId, isConstantsLoading])

  const loadMoreItems = (startIndex: number, stopIndex?: number) => {
    // loadMoreItems has trouble recognizing unloaded items at the start of the list
    // so if we're asking for a startIndex close to 0, we start from 0
    const offset = startIndex < 10 ? 0 : startIndex
    // need to calculate limit based on offset and stopIndex
    const limit = stopIndex
      ? stopIndex - offset + 1
      : Defaults.CONVERSATIONS_BATCH_LOAD_COUNT
    const action = clientId
      ? fetchMoreItemsForClientConversationsList(clientId, offset, limit)
      : fetchMoreItemsForConversationsList(offset, limit)
    dispatch(action)
  }

  return { loadMoreItems, clearFilters, applyFilter, filters }
}

export function useCreatedConversationsInfo({
  getIsConversationCreating,
  onConversationCreationSuccess,
  onConversationCreationFailure,
  onViewConversation = R.F,
  displayDefaultSuccessResult = true,
  createdInfoDialogProps = {},
}: {
  createdInfoDialogProps?: Partial<CreatedConversationsInfoDialogProps>
  displayDefaultSuccessResult?: boolean
  getIsConversationCreating: (state: RootState) => boolean
  onConversationCreationFailure?: (error: string) => void
  onConversationCreationSuccess?: () => void
  onViewConversation?: (conversationId: string) => void
}) {
  const navigate = useNavigate()
  const areConversationsCreated = useSelector(getAreConversationsCreated)
  const latestConversationError = useSelector(getConversationsError)
  const [openCreatedConversationsInfoDialog] = useDialog(
    DialogNames.CREATED_CONVERSATIONS_INFO_DIALOG,
  )

  const handleViewConversation = (conversationId: string) => {
    navigate(`${BaseRoute.COMMUNICATIONS}/${conversationId}`)
    onViewConversation(conversationId)
  }

  const conversationCreationSuccess = () => {
    if (displayDefaultSuccessResult) {
      openCreatedConversationsInfoDialog({
        ...createdInfoDialogProps,
        flow: CreatedConversationFlow.PLAIN_MESSAGE_SENT,
        handleViewConversation,
      })
    }

    if (onConversationCreationSuccess) {
      onConversationCreationSuccess()
    }
  }

  const conversationCreationFail = () => {
    if (onConversationCreationFailure) {
      onConversationCreationFailure(latestConversationError as string)
    }
  }

  const onConversationCreated = () => {
    if (areConversationsCreated) {
      conversationCreationSuccess()
    } else {
      conversationCreationFail()
    }
  }

  return useCloseAfterCreation(onConversationCreated, getIsConversationCreating)
}

export function useIncludeMedicalRecord() {
  const [medicalValidationError, setMedicalValidationError] = useState<
    string | undefined
  >()
  const isMarkMessagesAsMedicalEnabled = useSelector(
    getFeatureToggle(FeatureToggle.MARK_MESSAGES_AS_MEDICAL),
  )
  const isVeterinarian1 = useHasRole(RoleName.Veterinarian)
  const isVeterinarian2 = useHasRole(RoleName.Veterinarian2)
  const isVeterinarian = isVeterinarian1 || isVeterinarian2

  const isVetTech = useHasRole(RoleName.VetTech)
  const isVetAssistant = useHasRole(RoleName.VetAssistant)
  const isVetTechOrAssistant = isVetTech || isVetAssistant

  return {
    isMarkMessagesAsMedicalEnabled,
    shouldShowCheckbox:
      isMarkMessagesAsMedicalEnabled &&
      (isVeterinarian || isVetTechOrAssistant),
    defaultCheckboxValue: isMarkMessagesAsMedicalEnabled && isVeterinarian,
    medicalValidationError,
    setMedicalValidationError,
  }
}

export const useConversationTransportSelect = ({
  clientId,
  contactId,
}: {
  clientId: string | Nil
  contactId: string | Nil
}) => {
  const contact = useSelector(getContact(contactId))
  const client = useSelector(getUser(clientId))

  const { transport: preferredTransport } = useGetPreferredContactMethod({
    client,
  })

  const boopFlowEnabled = useBoopEnabled()

  const isBoopUser = Boolean(client?.isBoopUser)
  const isRecipientEmailExists =
    Boolean(contact?.email) || Boolean(client?.email)
  const isRecipientPhoneExists =
    Boolean(contact?.phone) || Boolean(client?.mobilePhone)

  const canSendSmsToClient = useCanSendSmsToClient(clientId)

  const canSendSmsToPerson = canSendSmsToClient || Boolean(contactId)

  return {
    preferredTransport,
    isBoopUser,
    isRecipientEmailExists,
    isRecipientPhoneExists,
    canSendSmsToPerson,
    boopFlowEnabled,
    clientPreferredContactMethodId: client?.preferredContactMethodId,
  }
}

export const useMessageTransportSelect = ({
  clientId,
  contactId,
}: {
  clientId: string | Nil
  contactId: string | Nil
}) => {
  const { t } = useTranslation(['Common', 'Communications', 'Clients'])
  const isSmsCommunicationsEnabled = useSelector(getSmsCommunicationsEnabled)
  const {
    preferredTransport,
    isBoopUser,
    isRecipientEmailExists,
    isRecipientPhoneExists,
    canSendSmsToPerson,
    boopFlowEnabled,
    clientPreferredContactMethodId,
  } = useConversationTransportSelect({ clientId, contactId })

  const isEmailDisabled = !isRecipientEmailExists
  const isSmsDisabled = !isRecipientPhoneExists || !canSendSmsToPerson
  const isBoopDisabled = !isBoopUser
  const hasPreferredTransport = Boolean(
    clientPreferredContactMethodId && preferredTransport,
  )

  const getAdditionalMessage = (
    transport: ConversationTransport | Nil,
    isPreferred: boolean,
  ) => {
    if (!transport) {
      return t(
        'Communications:MESSAGE_FORMATTING_AREA.NOT_SHARED_WITH_PET_PARENT',
      )
    }
    if (transport === ConversationTransport.Email && !isRecipientEmailExists) {
      return t('Communications:MESSAGE_FORMATTING_AREA.NO_EMAIL_PROVIDED')
    }
    if (transport === ConversationTransport.Sms && !isRecipientPhoneExists) {
      return t('Communications:MESSAGE_FORMATTING_AREA.NO_PHONE_PROVIDED')
    }
    if (transport === ConversationTransport.Sms && !canSendSmsToPerson) {
      return t('Communications:MESSAGE_FORMATTING_AREA.NOT_OPTED_IN')
    }
    if (transport === ConversationTransport.Boop && !isBoopUser) {
      return t('Communications:MESSAGE_FORMATTING_AREA.NOT_A_BOOP_USER')
    }
    if (isPreferred) {
      return t('Communications:MESSAGE_FORMATTING_AREA.PET_PARENT_PREFERRED')
    }
    return ''
  }

  const options = [
    {
      hidden: !isSmsCommunicationsEnabled,
      disabled: isSmsDisabled,
      transport: ConversationTransport.Sms,
    },
    {
      hidden: !boopFlowEnabled,
      disabled: isBoopDisabled,
      transport: ConversationTransport.Boop,
    },
    {
      hidden: false,
      disabled: isEmailDisabled,
      transport: ConversationTransport.Email,
    },
    {
      hidden: false,
      disabled: false,
      transport: ConversationTransport.LogPhoneCall,
    },
    { hidden: false, disabled: false, transport: undefined },
  ]

  const transportOptions = options.map(({ hidden, disabled, transport }) => {
    const isPreferred =
      preferredTransport === transport && hasPreferredTransport
    const additionalMessage = getAdditionalMessage(transport, isPreferred)

    return getTransportSelectOption(
      transport,
      additionalMessage,
      hidden,
      disabled,
    )
  })

  return {
    transportOptions,
    preferredTransport: hasPreferredTransport ? preferredTransport : undefined,
  }
}

export const useMessageFormatting = ({
  conversationId,
}: {
  conversationId: string
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isCommunicationRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
  )
  const isCvcSystemMessageSuppressionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_SYSTEM_MESSAGE_SUPPRESSION),
  )

  const isSuppressAllSystemMessagesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SUPPRESS_ALL_SYSTEM_MESSAGES),
  )

  const isLogicalSMSThreadingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.LOGICAL_SMS_THREADING),
  )
  const isUnifiedConversationTransportationThreadingEnabled = useSelector(
    getFeatureToggle(
      FeatureToggle.UNIFIED_CONVERSATION_TRANSPORTATION_THREADING,
    ),
  )
  const { t } = useTranslation(['Common', 'Communications', 'Clients'])

  const editMessage = useSelector(getEditMessage)
  const conversation = useSelector(getConversationById(conversationId))
  const latestMessageId = conversation?.messages?.[0]
  const latestMessage = useSelector(getMessage(latestMessageId))
  const isLatestMessageInbound = latestMessage?.way === MessageWay.INBOUND
  const files = useSelector(getAttachmentsToSend)
  const messageFormattingAreaExpanded = useSelector(
    getMessageFormattingAreaExpanded,
  )
  const appointment = useSelector(getTimetableEvent(conversation?.eventId))

  const conversationFormattingAreaRef =
    useRef<ConversationMessageFormattingAreaHandle>(null)

  const location = useLocation()
  const [openDialog] = useDialog(DialogNames.CONVERSATION_INTERNAL_NOTE)
  const [openNewThreadReplyDialog] = useDialog(DialogNames.NEW_THREAD_REPLY)

  const isLoading = !conversation
  const {
    state: status,
    transport,
    client: clientId,
    patient: patientId,
    contact: contactId,
  } = conversation || {}

  const isSystemMessage =
    isCvcSystemMessageSuppressionEnabled &&
    Boolean(conversation?.isSystemMessage)

  const isSystemMessageComposerSuppressed =
    isSystemMessage &&
    (transport === ConversationTransport.Email ||
      isSuppressAllSystemMessagesEnabled)

  const canSendSmsToClient = useCanSendSmsToClient(clientId)

  const {
    isMarkMessagesAsMedicalEnabled,
    shouldShowCheckbox,
    defaultCheckboxValue,
    medicalValidationError,
    setMedicalValidationError,
  } = useIncludeMedicalRecord()

  const { transportOptions, preferredTransport } = useMessageTransportSelect({
    clientId,
    contactId,
  })

  const {
    fields: { messageField, isMedical, transport: transportField },
    validate,
    reset,
  } = useFields([
    {
      name: 'messageField',
      label: t('Common:MESSAGE'),
      validators: ['required'],
      initialValue: editMessage?.body ?? '',
    },
    {
      name: 'isMedical',
      label: t('Communications:MEDICAL_RECORDS.INCLUDE_MESSAGE'),
      initialValue: editMessage?.isMedical ?? defaultCheckboxValue,
    },
    {
      name: 'transport',
      initialValue:
        editMessage && conversation?.transport ? conversation.transport : '',
      validators: isUnifiedConversationTransportationThreadingEnabled
        ? ['required']
        : undefined,
    },
  ])

  const {
    isDeliveryFailed,
    internalNoteOnly,
    canSendMessage,
    showSendButton,
    showReplyButton,
    showReplyInNewThreadButton,
  } = getConversationFormattingState({
    status,
    transport,
    isUnifiedConversationTransportationThreadingEnabled,
    isLogicalSMSThreadingEnabled,
    isMarkMessagesAsMedicalEnabled,
    messageFormattingAreaExpanded,
    canSendSmsToClient,
    isLatestMessageInbound,
    totalCount: conversation?.totalCount ?? 0,
    hasLatestMessage: Boolean(latestMessage),
  })

  const archiveActionTitle = getArchiveActionTitle({
    status,
    isLogicalSMSThreadingEnabled,
    t,
  })

  const goBack = () => navigate(-1)

  const onInternalNoteAdd = () => openDialog({ conversationId })

  const onArchiveAction = () =>
    handleArchiveToggle(status, conversationId, dispatch)

  const validateAndSend = (isDraft: boolean) => {
    const isValid = validateMessageSend(
      isDraft,
      isMedical.value,
      setMedicalValidationError,
      validate,
      t,
    )
    if (!isValid) return

    dispatch(
      getMessageAction({
        editMessage,
        isDraft,
        conversationId,
        messageFieldValue: messageField.value,
        files,
        isMedicalValue: isMedical.value,
      }),
    )
    reset()
  }

  const handleMessageSend = () => validateAndSend(false)

  const handleMessageDraftSend = () => validateAndSend(true)

  const onMarkUnread = () => {
    dispatch(setIsReadForConversation(conversationId, false))
    goBack()
  }

  const handleCloseMessageFormattingArea = () => {
    dispatch(closeMessageFormattingArea())
  }

  const handleReplyClick = () => {
    dispatch(messageFormattingAreaFocused())
  }

  const handleReplyInNewThreadClick = () =>
    openNewThreadReply(latestMessage, conversation, openNewThreadReplyDialog)

  const setMessageText = (text: string | Nil) => {
    messageField.setValue(text)
    conversationFormattingAreaRef.current?.resetMessageState()
  }

  useEffect(() => {
    syncPreferredTransport(
      editMessage,
      transportField.value,
      preferredTransport,
      transportField.setValue,
    )
  }, [preferredTransport, editMessage])

  useEffect(() => {
    setMessageText(editMessage?.body)
  }, [editMessage?.body])

  useEffect(() => {
    syncIsMedicalValue(
      editMessage?.isMedical,
      defaultCheckboxValue,
      isMedical.setValue,
    )
  }, [defaultCheckboxValue, editMessage?.isMedical])

  useEffect(() => {
    handleInitialLocationState(
      location,
      dispatch,
      navigate,
      handleCloseMessageFormattingArea,
    )
  }, [])

  return {
    messageFormattingAreaExpanded,
    handleCloseMessageFormattingArea,
    canSendMessage,
    clientId,
    conversation,
    isCommunicationRightRailEnabled,
    messageField,
    patientId,
    appointment,
    conversationFormattingAreaRef,
    transport,
    isMarkMessagesAsMedicalEnabled,
    dispatch,
    internalNoteOnly,
    showSendButton,
    isLoading,
    handleMessageSend,
    showReplyButton,
    handleReplyClick,
    showReplyInNewThreadButton,
    handleReplyInNewThreadClick,
    handleMessageDraftSend,
    shouldShowCheckbox:
      shouldShowCheckbox && transportField.value !== MessageType.INTERNAL_NOTE,
    isMedical,
    onInternalNoteAdd,
    isDeliveryFailed,
    onMarkUnread,
    onArchiveAction,
    archiveActionTitle,
    isSystemMessage,
    isSystemMessageComposerSuppressed,
    messageError: medicalValidationError,
    transportField,
    transportOptions,
    getSendActionName: () => getSendActionName(transportField.value, t),
  }
}

export const useChatSenderRecipientInfo = ({
  message,
  recipient,
}: {
  message: ChatMessage
  recipient: Recipient | Nil
}) => {
  const dispatch = useDispatch()
  const practiceCreator = useSelector(getUser(message.creatorId))
  const practice = useSelector(getBusiness(message.businessId))
  const fetchingCreatorState = useSelector(
    getMembersIsFetchingMemberById(message.creatorId),
  )
  const isFetchingCreator = fetchingCreatorState?.isLoading
  const fetchCreatorError = fetchingCreatorState?.error
  const isClientCommunication = recipient?.type === Type.Client
  const business = useSelector(getBusiness(recipient?.businessId))
  const contact = useSelector(getContact(recipient?.contactId))
  const client = useSelector(getUser(recipient?.clientId))
  const isOutboundMessage = message.way === MessageWay.OUTBOUND

  const hasCreator = message.isSystemMessage || !R.isNil(practiceCreator)

  useEffect(() => {
    if (
      !hasCreator &&
      message.creatorId &&
      !isFetchingCreator &&
      !fetchCreatorError &&
      isOutboundMessage
    ) {
      dispatch(fetchMember(message.creatorId))
    }
  }, [message.creatorId, isFetchingCreator])

  const inboundSender =
    getRecipientName({
      client,
      contact,
      business,
      email: recipient?.email,
      phoneNumber: recipient?.phoneNumber,
      recipientType: recipient?.type || Type.Direct,
      transport: message.transport,
    }) || ''
  const outboundSender =
    getPersonString(practiceCreator) || practice?.name || ''
  const sender = isOutboundMessage ? outboundSender : inboundSender

  return {
    sender,
    isClientCommunication,
  }
}
